import { Error, ExpandCircleDown } from '@mui/icons-material'
import { Accordion, AccordionSummary, Box, Typography, AccordionDetails, type SxProps, Stack } from '@mui/material'
import { BudgetActivityDisplay } from './BudgetActivityDisplay'
import { useEffect, useState } from 'react'
import { BudgetISDAccordion } from './BudgetISDAccordion'
import { RevisionRequestDisplay } from './RevisionRequestDisplay'
import { type PlanRevisionRequest } from '../../../../models/PlanRevisionRequest'
import { Comments } from '../../../customComponents/comments/Comments'
import { CommentParentType } from '../../../../models/Comment'
import { type AnnualPlanRole } from '../../../../models/AnnualPlanRole'
import {
  type PlanOrganizationTask,
  PlanOrganizationTaskName, type PlanOrganizationTaskStatus
} from '../../../../models/PlanOrganizationTask'
import { GetPlanOrganizationTask } from '../../../../services/AnnualPlanService'
import { useUpdatePlanOrgTaskStatus } from '../../../../hooks/use-updatePlanOrgTaskStatus'
import {
  RCNOperationsStatusButton
} from '../priorityActivities/rcnOperations/RCNOperationsStatusButton'
import type { PriorityActivity } from '../../../../models/PriorityActivity'
import { SurpassesThreshold } from './SurpassesThreshold'
import { handleApiResponse } from '../../../../utils/apiHelpers'

export interface ISDSummary {
  isdName: string
  isdImageKey?: string
  totalAmount: number
  totalUpdatedAmount: number
  planOrganizationId?: number
  planOrganizationTask?: PlanOrganizationTask
  priorityId?: number
  priorityNumber?: number
  priorityActivities?: PriorityActivity[]
  annualPlanId: number
  rcnId: number
  needsReview: boolean
  budgetItems: BudgetItem[]
}

export interface BudgetItem {
  name: string
  amount: number
  updatedAmount: number
  needsReview?: boolean
  color: string
  annualPlanId?: number
  rcnId?: number
  viewDetails: (() => void) | null
}

interface Props {
  priorityName: string
  totalAmount: number
  totalUpdatedAmount: number
  isdSummaries: ISDSummary[]
  topLevelBudgetItems?: BudgetItem[]
  meetingTaskId?: number
  revisionRequest?: PlanRevisionRequest
  dataTestId?: string
  sx?: SxProps
  readOnly: boolean
  onResolveRevisionRequest: () => void
  userRole: AnnualPlanRole | null
  rcnId?: number
  annualPlanId?: number
  hideButtons?: boolean
  implementationPhase?: boolean
}

export const BudgetAccordion = (props: Props): JSX.Element => {
  const [expanded, setExpanded] = useState(false)
  const [planOrganizationTask, setPlanOrganizationTask] = useState<PlanOrganizationTask>()
  const { disableStatus } = useUpdatePlanOrgTaskStatus({
    userRole: props.userRole,
    annualPlanId: props.annualPlanId ?? 0,
    planOrganizationTask,
    contractPhase: true
  })

  useEffect(() => {
    const fetchTask = async (): Promise<void> => {
      if (props.meetingTaskId == null) return
      if (props.annualPlanId == null || props.rcnId == null) return
      const task = await handleApiResponse(GetPlanOrganizationTask(props.annualPlanId, props.rcnId, PlanOrganizationTaskName.RcnOperations))
      if (task != null) setPlanOrganizationTask(task)
    }

    void fetchTask()
  }, [props.meetingTaskId, props.annualPlanId, props.rcnId])

  const handleStatusChange = async (status: PlanOrganizationTaskStatus): Promise<void> => {
    if (planOrganizationTask == null) return
    const updatedTask = { ...planOrganizationTask, status }
    setPlanOrganizationTask(updatedTask)
  }

  const showRevisionRequest = props.revisionRequest != null && !props.revisionRequest.resolved

  const hasItemsNeedingReview = props.implementationPhase === true && (
    (props.topLevelBudgetItems?.some(item => item.needsReview === true) ?? false) ||
    props.isdSummaries.some(isd => isd.budgetItems.some(item => item.needsReview === true))
  )

  return <>
    {!expanded && showRevisionRequest &&
      <Box
        sx={{ bgcolor: 'var(--grey-100)', pl: '16px', pt: '10px', borderTop: '1px solid var(--grey-300)' }}
        data-testid={`revisionRequestShort-${props.priorityName}`}
      >
        <Box
          sx={{
            px: '17px',
            py: '5px',
            borderRadius: '30px',
            bgcolor: 'var(--red-700)',
            display: 'flex',
            alignItems: 'center',
            textWrap: 'nowrap',
            width: 'fit-content'
          }}
        >
          <Error sx={{ color: 'white', width: '20px', height: '20px' }} />

          <Typography variant='h5' sx={{ color: 'white', ml: '.25em' }}>
            Needs revision
          </Typography>
        </Box>
      </Box>
    }

    {expanded && showRevisionRequest &&
      <Box
        sx={{
          p: '16px',
          pb: 0,
          borderTop: '6px solid var(--grey-300)'
        }}
        data-testid={`revisionRequestExpanded-${props.priorityName}`}
      >
        <RevisionRequestDisplay
          revisionRequest={props.revisionRequest}
          readOnly={props.readOnly}
          onSave={props.onResolveRevisionRequest}
        />
      </Box>
    }

    <Accordion
      expanded={expanded}
      disableGutters={true}
      onChange={() => { setExpanded(!expanded) }}
      data-testid={`accordion-${props.priorityName}`}
      sx={{
        bgcolor: expanded ? 'white' : 'var(--grey-100)',
        borderTop: expanded && !showRevisionRequest ? '6px solid var(--grey-300)' : undefined,
        borderBottom: expanded ? '6px solid var(--grey-300)' : undefined,
        '&:before': showRevisionRequest ? { display: 'none' } : undefined,
        boxShadow: 'none',
        breakInside: 'avoid',
        ...props.sx
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandCircleDown sx={{ color: 'var(--blue-700)', visibility: props.hideButtons === true ? 'hidden' : 'visible' }} data-testid={props.dataTestId} />}
        role='none presentation'
        aria-expanded={undefined}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.5em' }}>
            {props.implementationPhase === true && hasItemsNeedingReview && !expanded &&
              <SurpassesThreshold/>
            }
            <Typography variant='h3' >
              {props.priorityName}
            </Typography>

            {expanded &&
              <Typography variant='body1' sx={{ fontSize: '16px', color: 'var(--grey-700)' }}>
                Budget requests{props.isdSummaries.length > 0 && ' broken down by ISDs and Districts'}
              </Typography>
            }
          </Box>

          {(!expanded || props.hideButtons === true) &&
            (props.implementationPhase === true
              ? (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant='h3' data-testid={`totalPriorityAmount-${props.priorityName}`}>
                    Contract: {props.totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </Typography>
                  <Typography variant='h4' data-testid={`totalPriorityUpdatedAmount-${props.priorityName}`}>
                    Updates: {props.totalUpdatedAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </Typography>
                </Box>
                )
              : (
                <Typography variant='h3' data-testid={`totalPriorityAmount-${props.priorityName}`}>
                  {props.totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </Typography>
                )
            )
           }

          {expanded && props.meetingTaskId != null && props.hideButtons !== true &&
            <Box sx={{ display: 'flex', alignItems: 'center', mr: '1em' }}>
              {planOrganizationTask != null && (
                <RCNOperationsStatusButton
                  planOrganizationTask={planOrganizationTask}
                  userRole={props.userRole}
                  handleStatusChange={handleStatusChange}
                  disableStatus={disableStatus}
                  annualPlanId={props.annualPlanId ?? 0}
                />
              )}

              <Comments
                parentType={CommentParentType.PlanOrganizationTask}
                parentId={props.meetingTaskId}
              />
            </Box>
           }
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ breakInside: 'avoid' }}>
        <Stack spacing='1em'>
          {props.topLevelBudgetItems?.map((item, index) => (
            <BudgetActivityDisplay
              key={index}
              name={item.name}
              amount={item.amount}
              updatedAmount={item.updatedAmount}
              needsReview={item.needsReview}
              viewDetails={item.viewDetails}
              color={item.color}
              planOrganizationId={item.rcnId ?? 0}
              priorityId={props.meetingTaskId ?? 0}
              annualPlanId={item.annualPlanId ?? 0}
              rcnId={item.rcnId ?? 0}
              userRole={props.userRole}
              hideButtons={props.hideButtons}
              implementationPhase={props.implementationPhase}
            />
          ))}

          {props.isdSummaries.map((summary, index) => (
            <BudgetISDAccordion
              key={index}
              summary={summary}
              userRole={props.userRole}
              hideButtons={props.hideButtons}
              implementationPhase={props.implementationPhase}
            />
          ))}

          <Typography variant='h2' sx={{ textAlign: 'right' }} data-testid='totalPriorityAmountDetails'>
            Total: {props.totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
          </Typography>
          {props.implementationPhase === true &&
            <Typography variant='h3' sx={{ textAlign: 'right' }} data-testid='totalUpdatePriorityAmountDetails'>Updates Total: {props.totalUpdatedAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</Typography>
          }
        </Stack>
      </AccordionDetails>
    </Accordion>
  </>
}
